import { defineStore } from 'pinia'
import { ref } from 'vue'
import { getDictionaryItems as apiGetDictionaryItems } from '@/api/dictionary'
import type { SysDictionaryItem } from '@/types/entities'

/**
 * 字典项内存缓存 store。
 * 缓存按 dictionaryCode 索引，避免同一字典被多个组件重复请求。
 * 缓存存储完整数据（含 name / nameEn），由 dictItemLabel 按 locale 选取。
 */
export const useDictionaryStore = defineStore('dictionary', () => {
  const cache = ref<Map<string, SysDictionaryItem[]>>(new Map())
  const loading = ref<Set<string>>(new Set())

  async function getItems(code: string): Promise<SysDictionaryItem[]> {
    if (cache.value.has(code)) {
      return cache.value.get(code)!
    }
    if (loading.value.has(code)) {
      // 已有在途请求，等待结果
      while (loading.value.has(code)) {
        await new Promise(resolve => setTimeout(resolve, 50))
      }
      return cache.value.get(code) || []
    }
    loading.value.add(code)
    try {
      const items = await apiGetDictionaryItems(code)
      // 整体替换 Map 引用以触发响应式更新（render 中的同步读取能感知后续加载完成）
      cache.value = new Map(cache.value).set(code, items || [])
      return items || []
    } finally {
      loading.value.delete(code)
    }
  }

  function preload(code: string, items: SysDictionaryItem[]): void {
    cache.value = new Map(cache.value).set(code, items)
  }

  function clear(): void {
    cache.value.clear()
  }

  return { cache, getItems, preload, clear }
})
